<template>
    <div class="fixed bottom-0 flex flex-row-reverse items-end gap-2 inset-x-2">
        <Chat v-for="chat in displayedChats" :key="chat.id" :chat="chat" :frontend="false" :boxed="true"> </Chat>
        <div
            v-if="collapsedChats.length > 0"
            class="relative px-4 py-2 font-medium border-t rounded-t cursor-pointer hover:bg-brand-700 border-brand-400"
            :class="showSelection ? 'bg-brand-700' : 'bg-brand-600'"
            @click="toggleSelection"
        >
            +{{ collapsedChats.length }}

            <div
                v-show="showSelection"
                class="absolute top-0 right-0 overflow-y-auto -translate-y-full bg-white border divide-y shadow max-h-40 w-84"
            >
                <div
                    v-for="chat in collapsedChats"
                    :key="chat.id"
                    class="flex items-center px-4 py-2 hover:bg-gray-50"
                    @click="prioritizeChat(chat.id)"
                >
                    <div class="w-12">
                        <chat-status :status="chat.status"></chat-status>
                    </div>
                    <div class="w-64 text-sm">
                        <p class="font-bold">{{ chat.label }}</p>
                        <p class="text-gray-600 truncate">{{ chat.last_message_text }}</p>
                    </div>
                    <div class="w-4 text-xs text-center">
                        {{ chat.messages.filter((item) => item.user_id === null).length }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import Chat from '@src/Chat/Pages/Chat/Chat.vue';
import ChatStatus from '@src/Chat/Pages/Chat/ChatStatus.vue';
import { usePage } from '@inertiajs/vue3';

const numberOfWindows = 2;

const chats = ref([]);

const expanded = ref([]);

const showSelection = ref(false);

const getAdminChats = () => {
    axios.get(route('chat.admin.chats.active'))
        .then(({ data }) => {
            chats.value = Array.isArray(data.chats) ? data.chats : [];
            conditionallySelectExpanded();
        })
        .catch((error) => {
            console.error('Failed to fetch admin chats:', error);
        });
};

const displayedChats = computed(() => {
    return chats.value
        .filter((item) => expanded.value.includes(item.id))
        .sort((a, b) => expanded.value.indexOf(a.id) - expanded.value.indexOf(b.id));
});

const collapsedChats = computed(() => {
    return chats.value.filter((item) => !expanded.value.includes(item.id));
});

const toggleSelection = () => {
    showSelection.value = !showSelection.value;
};

getAdminChats();

const selectExpanded = () => {
    if (!chats.value || !Array.isArray(chats.value)) {
        return;
    }

    const sorted = [...chats.value].sort((a, b) => {
        if (a.updated_at < b.updated_at) {
            return -1;
        }
        if (a.updated_at > b.updated_at) {
            return 1;
        }
        return 0;
    });

    expanded.value = Object.values(sorted.slice(0, numberOfWindows).map((item) => item.id));
};

const conditionallySelectExpanded = () => {
    if (expanded.value && expanded.value.length === 0) {
        selectExpanded();
    }
};

const prioritizeChat = (id: number): boolean => {
    if (expanded.value.includes(id)) {
        return true;
    }

    if (expanded.value.length < numberOfWindows) {
        expanded.value.unshift(id);
        return true;
    }

    delete expanded.value[numberOfWindows - 1];
    expanded.value.unshift(id);

    return true;
};

/**
 * Refresh chat list
 */
let onlineReportInterval = null;
let refreshChats = null;

const reportOnline = async () => {
    return axios.post(route('team.users.online'), { users: [usePage().props.auth.user.id] });
};

onMounted(() => {
    refreshChats = setInterval(getAdminChats, 30 * 1000);

    reportOnline();
    onlineReportInterval = setInterval(() => {
        reportOnline();
    }, 30 * 1000);
});

onUnmounted(() => {
    clearInterval(refreshChats);
    clearInterval(onlineReportInterval);
});

window.Echo.channel('chat')
    .listen('.chat.created', () => {
        getAdminChats();
    })
    .listen('.chat.idled', () => {
        getAdminChats();
    })
    .listen('.chat.updated', () => {
        getAdminChats();
    })
    .listen('.chat.reopened', () => {
        getAdminChats();
    })
    .listen('.chat.closed', () => {
        getAdminChats();
    });

/**
 * Online users
 */
window.Echo.join('online')
    .here((users) => {
        axios.post(route('team.users.online'), { users: users.map(({ id }) => id) });
    })
    .leaving((user) => {
        axios.post(route('team.users.offline'), { users: [user.id] });
    });

window.Echo.connector.pusher.connection.bind('disconnected', () => {
    axios.post(route('team.users.offline'), { users: [usePage().props.auth.user.id] });
});
</script>
